import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import {
  STATUS_OPTION_ITEM_CLASS,
  StatusOptionContent,
} from "./StatusOptionContent";

interface OrdersBulkBarProps {
  scriptCount: number;
  orderCount?: number;
  statusOptions: readonly MarketplaceFulfillmentStatus[];
  applying: boolean;
  onSetStatus: (status: MarketplaceFulfillmentStatus) => void;
  onCopyOrderIds?: () => void;
  showStatusAction?: boolean;
  onClear: () => void;
}

/** Replaces the toolbar while scripts are selected — one job at a time. */
export function OrdersBulkBar({
  scriptCount,
  orderCount,
  statusOptions,
  applying,
  onSetStatus,
  onCopyOrderIds,
  showStatusAction = true,
  onClear,
}: OrdersBulkBarProps) {
  const { t } = useTranslation();

  return (
    <div
      role="toolbar"
      aria-label={t("marketplace.orders.list.bulk.toolbarLabel")}
      className="flex min-h-8 flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary-subtle/50 px-3 py-1.5"
    >
      <p className="text-xs font-medium text-foreground" aria-live="polite">
        {orderCount == null
          ? t("marketplace.orders.list.bulk.selected", { count: scriptCount })
          : t("marketplace.orders.list.bulk.selectedOrders", {
              count: orderCount,
            })}
        {orderCount != null && scriptCount > 0 ? (
          <span className="ml-1 font-normal text-muted-foreground">
            {t("marketplace.orders.list.bulk.scriptsAffected", {
              count: scriptCount,
            })}
          </span>
        ) : null}
      </p>

      {showStatusAction ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              size="sm"
              iconEnd="chevron-down"
              disabled={applying || statusOptions.length === 0}
              aria-busy={applying || undefined}
              className="h-7 text-xs"
            >
              {applying ? (
                <>
                  <Icon name="loader" size="sm" color="inherit" className="animate-spin" aria-hidden />
                  {t("marketplace.orders.list.bulk.applying")}
                </>
              ) : (
                t("marketplace.orders.list.bulk.setStatus")
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-72">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t("marketplace.orders.list.bulk.setStatusHeading", { count: scriptCount })}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {statusOptions.map((status) => (
              <DropdownMenuItem
                key={status}
                className={STATUS_OPTION_ITEM_CLASS}
                onSelect={() => onSetStatus(status)}
              >
                <StatusOptionContent status={status} />
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      {onCopyOrderIds ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          iconStart="copy"
          className="h-7 text-xs"
          onClick={onCopyOrderIds}
        >
          {t("marketplace.orders.list.bulk.copyOrderIds")}
        </Button>
      ) : null}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="ml-auto h-7 text-xs text-muted-foreground"
        onClick={onClear}
      >
        {t("marketplace.orders.list.bulk.clear")}
        <kbd className="ml-1 rounded-sm border border-border px-1 font-sans text-[10px] text-muted-foreground">
          Esc
        </kbd>
      </Button>
    </div>
  );
}
